import * as protos from "../../assets/proto/compiled/all";
import { Util } from "./util";

export interface Function<T, R> {
  (input: T): R;
}

export class Formatter {


    static getText(textProto: protos.waiternow.common.ITextProto | null | undefined, language: protos.waiternow.common.Language): string {
        if (!textProto) {
            return '';
        }

        if (!language || textProto.language == language) {
            return Util.safeString(textProto.text);
        }

        if (textProto.localizedTexts) {
            for(const localizedText of textProto.localizedTexts) {
                if (localizedText.language == language) {
                    return Util.safeString(localizedText.text);
                }
            }
        }

        return Util.safeString(textProto.text);
    }

    static getNullableText(textProto: protos.waiternow.common.ITextProto | null | undefined, language: protos.waiternow.common.Language): string {
        if (!textProto) {
            return '';
        }

        if (!language || textProto.language == language) {
            return Util.safeString(textProto.text);
        }

        if (textProto.localizedTexts) {
            for(const localizedText of textProto.localizedTexts) {
                if (localizedText.language == language) {
                    return Util.safeString(localizedText.text);
                }
            }
        }

        return '';
    }

    static formatMoney(money: protos.waiternow.common.IMoneyProto | null | undefined): string {
        if (!money) {
            return "$0.00";
        }
        let str = '$';
        if (money.units) {
            str += money.units;
        } else {
            str += '0';
        }
        str += '.';
        if (!money.cents || money.cents == 0) {
            str += '00';
        }  else if(money.cents < 10) {
            str += '0' + money.cents;
        } else {
            str += money.cents;
        }
        return str;
    }

    static formatTimeProto(time: protos.waiternow.common.ITimeProto | null | undefined) {
        if (!time) {
            return '';
        }
        const hour: number = time.hour || 0;
        const minute: number = time.minute || 0;
        const second: number = time.second || 0;
        let text = hour.toString().padStart(2, '0') + ':' + minute.toString().padStart(2, '0');
        if (second > 0) {
            text += ':' + second.toString().padStart(2, '0');
        }
    return text;
    }

    static formatDateTimeProto(dateTime: protos.waiternow.common.IDateTimeProto | null | undefined) {
        if (!dateTime) {
            return '';
        }
        let text = Formatter.formatDateProto(dateTime.date);
        if (dateTime.time) {
            text += ' ' + Formatter.formatTimeProto(dateTime.time);
        }
        return text;
    }

    static formatDateProto(date: protos.waiternow.common.IDateProto | null | undefined) {
        if (!date || !date.year || !date.month || !date.day) {
            return '';
        }
        return date.year.toString() + '-' + date.month.toString().padStart(2, '0') + '-' + date.day.toString().padStart(2, '0');
    }

    static formatMonth(month: protos.waiternow.common.Month | null | undefined, language?: protos.waiternow.common.Language): string {
        if (!month) {
            return '';
        }
        const isSpanish = language == protos.waiternow.common.Language.SPANISH;

        switch (month) {
            case protos.waiternow.common.Month.JANUARY:
                return isSpanish ? 'Enero' : 'January';
            case protos.waiternow.common.Month.FEBRUARY:
                return isSpanish ? 'Febrero' : 'February';
            case protos.waiternow.common.Month.MARCH:
                return isSpanish ? 'Marzo' : 'March';
            case protos.waiternow.common.Month.APRIL:
                return isSpanish ? 'Abril' : 'April';
            case protos.waiternow.common.Month.MAY:
                return isSpanish ? 'Mayo' : 'May';
            case protos.waiternow.common.Month.JUNE:
                return isSpanish ? 'Junio' : 'June';
            case protos.waiternow.common.Month.JULY:
                return isSpanish ? 'Julio' : 'July';
            case protos.waiternow.common.Month.AUGUST:
                return isSpanish ? 'Agosto' : 'August';
            case protos.waiternow.common.Month.SEPTEMBER:
                return isSpanish ? 'Septiembre' : 'September';
            case protos.waiternow.common.Month.OCTOBER:
                return isSpanish ? 'Octubre' : 'October';
            case protos.waiternow.common.Month.NOVEMBER:
                return isSpanish ? 'Noviembre' : 'November';
            case protos.waiternow.common.Month.DECEMBER:
                return isSpanish ? 'Diciembre' : 'December';
        }
    }

    static formatSchedule(schedule: protos.waiternow.common.IScheduleProto | null | undefined, language: protos.waiternow.common.Language): string {
        if (!schedule) {
            return '';
        }

        const isSpanish = language == protos.waiternow.common.Language.SPANISH;

        const lines: Array<string> = [];

        const timeRangeFormatter: Function<protos.waiternow.common.ITimeRangeProto | null | undefined, string> = timeRange => {
            if (!timeRange) {
                return '';
            }
            let text = '';
            if (timeRange.from && timeRange.to) {
                text += isSpanish ? 'de ' : 'from '
                text += Formatter.formatTimeProto(timeRange.from);
                text += isSpanish ? ' a ' : ' to '
                text += Formatter.formatTimeProto(timeRange.to);
            } else if (timeRange.from && !timeRange.to) {
                text += isSpanish ? 'desde las ' : 'from '
                text += Formatter.formatTimeProto(timeRange.from);
            } else if (!timeRange.from && timeRange.to) {
                text += isSpanish ? 'hasta las ' : 'until '
                text += Formatter.formatTimeProto(timeRange.to);
            }
            return text;
        }

        for (const dateTimeRange of schedule.dateTimeRanges || []) {
            let text = isSpanish ? 'Del ' : 'From ';
            text += Formatter.formatDateTimeProto(dateTimeRange.from);
            text += isSpanish ? ' al ' : ' to ';
            text += Formatter.formatDateTimeProto(dateTimeRange.to);
            lines.push(text);
        }

        for (const dailyRecurrence of schedule.dailyRecurrences || []) {
            let text = '';
            switch(dailyRecurrence.recurrenceType) {
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.DAILY:
                    text = isSpanish ? 'Diario ' : 'Daily ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.WEEKDAYS:
                    text = isSpanish ? 'Entre semana ' : 'Weekdays ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.WEEKENDS:
                    text = isSpanish ? 'Fin de semana ' : 'Weekends ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.WEEKLY_ON_SUNDAY:
                    text = isSpanish ? 'Domingos ' : 'Sundays ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.WEEKLY_ON_MONDAY:
                    text = isSpanish ? 'Lunes ' : 'Mondays ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.WEEKLY_ON_TUESDAY:
                    text = isSpanish ? 'Martes ' : 'Tuesdays ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.WEEKLY_ON_WEDNESDAY:
                    text = isSpanish ? 'Miércoles ' : 'Wednesdays ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.WEEKLY_ON_THURSDAY:
                    text = isSpanish ? 'Jueves ' : 'Thursdays ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.WEEKLY_ON_FRIDAY:
                    text = isSpanish ? 'Viernes ' : 'Fridays ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.WEEKLY_ON_SATURDAY:
                    text = isSpanish ? 'Sábados ' : 'Saturdays ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_FIRST_SUNDAY:
                    text = isSpanish ? 'Primer Domingo del mes ' : 'First Sunday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_FIRST_MONDAY:
                    text = isSpanish ? 'Primer Lunes del mes ' : 'First Monday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_FIRST_TUESDAY:
                    text = isSpanish ? 'Primer Martes del mes ' : 'First Tuesday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_FIRST_WEDNESDAY:
                    text = isSpanish ? 'Primer Miércoles del mes ' : 'First Wednesday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_FIRST_THURSDAY:
                    text = isSpanish ? 'Primer Jueves del mes ' : 'First Thursday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_FIRST_FRIDAY:
                    text = isSpanish ? 'Primer Viernes del mes ' : 'First Friday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_FIRST_SATURDAY:
                    text = isSpanish ? 'Primer Sábado del mes ' : 'First Saturday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_SECOND_SUNDAY:
                    text = isSpanish ? 'Segundo Domingo del mes ' : 'Second Sunday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_SECOND_MONDAY:
                    text = isSpanish ? 'Segundo Lunes del mes ' : 'Second Monday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_SECOND_TUESDAY:
                    text = isSpanish ? 'Segundo Martes del mes ' : 'Second Tuesday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_SECOND_WEDNESDAY:
                    text = isSpanish ? 'Segundo Miércoles del mes ' : 'Second Wednesday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_SECOND_THURSDAY:
                    text = isSpanish ? 'Segundo Jueves del mes ' : 'Second Thursday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_SECOND_FRIDAY:
                    text = isSpanish ? 'Segundo Viernes del mes ' : 'Second Friday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_SECOND_SATURDAY:
                    text = isSpanish ? 'Segundo Sábado del mes ' : 'Second Saturday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_THIRD_SUNDAY:
                    text = isSpanish ? 'Tercer Domingo del mes ' : 'Third Sunday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_THIRD_MONDAY:
                    text = isSpanish ? 'Tercer Lunes del mes ' : 'Third Monday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_THIRD_TUESDAY:
                    text = isSpanish ? 'Tercer Martes del mes ' : 'Third Tuesday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_THIRD_WEDNESDAY:
                    text = isSpanish ? 'Tercer Miércoles del mes ' : 'Third Wednesday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_THIRD_THURSDAY:
                    text = isSpanish ? 'Tercer Jueves del mes ' : 'Third Thursday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_THIRD_FRIDAY:
                    text = isSpanish ? 'Tercer Viernes del mes ' : 'Third Friday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_THIRD_SATURDAY:
                    text = isSpanish ? 'Tercer Sábado del mes ' : 'Third Saturday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_LAST_SUNDAY:
                    text = isSpanish ? 'Último Domingo del mes ' : 'Last Sunday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_LAST_MONDAY:
                    text = isSpanish ? 'Último Lunes del mes ' : 'Last Monday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_LAST_TUESDAY:
                    text = isSpanish ? 'Último Martes del mes ' : 'Last Tuesday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_LAST_WEDNESDAY:
                    text = isSpanish ? 'Último Miércoles del mes ' : 'Last Wednesday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_LAST_THURSDAY:
                    text = isSpanish ? 'Último Jueves del mes ' : 'Last Thursday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_LAST_FRIDAY:
                    text = isSpanish ? 'Último Viernes del mes ' : 'Last Friday of the month ';
                break;
                case protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.MONTHLY_ON_LAST_SATURDAY:
                    text = isSpanish ? 'Último Sábado del mes ' : 'Last Saturday of the month ';
                break;
            }
            text += timeRangeFormatter(dailyRecurrence.timeRange);
            lines.push(text);
        }

        for (const monthlyRecurrence of schedule.monthlyRecurrences || []) {
            let text = isSpanish ? 'Mensual el dia ' : 'Monthly on day ';
            text += monthlyRecurrence.montlyOnDay;
            if (monthlyRecurrence.timeRange) {
                text += ' ' + timeRangeFormatter(monthlyRecurrence.timeRange);
            }
            lines.push(text);
        }

        for (const yearlyRecurrence of schedule.yearlyRecurrences || []) {
            let text = isSpanish ? 'Anual' : 'Yearly';
            if (yearlyRecurrence.day) {
                text += isSpanish ? ' en ' : ' on ';
                text += Formatter.formatMonth(yearlyRecurrence.month, language);
                text += ' ' + yearlyRecurrence.day;
            } else {
                text += isSpanish ? ' en ' : ' on ';
                text += Formatter.formatMonth(yearlyRecurrence.month, language);
            }
            if (yearlyRecurrence.timeRange) {
                text += ' ' + timeRangeFormatter(yearlyRecurrence.timeRange);
            }
            lines.push(text);
        }

        // let text = lines.join('\n');
        let text = lines.join(', ');

        if (schedule.isInverted) {
            text = 'No (' + text + ')';
        }

        return text;
    }
}